import React, { useState, useEffect } from 'react';
import { Lock, Mail, AlertCircle, Loader2, Eye, EyeOff } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import logo from '../../assets/logo.png';
import './Auth.css';

interface LoginPageProps {
    onLoginSuccess: () => void;
    onNeedsSetup: (email: string, token: string) => void;
    onNeeds2FA: (userId: string, password?: string) => void;
    onForgotPassword: () => void;
}

export default function LoginPage({ onLoginSuccess, onNeedsSetup, onNeeds2FA, onForgotPassword }: LoginPageProps) {
    const { login } = useApp();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        // Add noindex, nofollow meta tag to prevent crawler indexing
        const meta = document.createElement('meta');
        meta.name = 'robots';
        meta.content = 'noindex, nofollow';
        document.head.appendChild(meta);

        return () => {
            // Clean up meta tag on unmount
            document.head.removeChild(meta);
        };
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setIsLoading(true);

        try {
            const response = await login(email, password);

            if (response.requires_2fa && response.user_id) {
                onNeeds2FA(response.user_id, password);
            } else if (response.success) {
                onLoginSuccess();
            }
        } catch (err: any) {
            const data = err.response?.data;
            if (data?.needs_setup) {
                onNeedsSetup(email, data.invite_token);
            } else if (data?.locked) {
                setError(`Account locked. Try again in ${data.remainingMinutes} minutes.`);
            } else {
                setError(data?.error || 'Login failed');
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="auth-container">
            <div className="auth-card">
                <div className="auth-header">
                    <div className="auth-logo">
                        <img src={logo} alt="Tock Logo" />
                    </div>
                    <h1>Welcome to Tock</h1>
                    <p>Sign in to your account</p>
                </div>

                <form onSubmit={handleSubmit} className="auth-form">
                    {error && (
                        <div className="auth-error">
                            <AlertCircle size={16} />
                            <span>{error}</span>
                        </div>
                    )}

                    <div className="auth-field">
                        <label htmlFor="email">Email</label>
                        <div className="auth-input-wrapper">
                            <Mail size={18} />
                            <input
                                id="email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@example.com"
                                required
                                disabled={isLoading}
                            />
                        </div>
                    </div>

                    <div className="auth-field">
                        <label htmlFor="password">Password</label>
                        <div className="auth-input-wrapper">
                            <Lock size={18} />
                            <input
                                id="password"
                                type={showPassword ? 'text' : 'password'}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••"
                                required
                                disabled={isLoading}
                            />
                            <button
                                type="button"
                                className="auth-toggle-password"
                                onClick={() => setShowPassword(!showPassword)}
                            >
                                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>
                    </div>

                    <button type="submit" className="auth-submit" disabled={isLoading}>
                        {isLoading ? (
                            <>
                                <Loader2 size={18} className="spin" />
                                Signing in...
                            </>
                        ) : (
                            'Sign In'
                        )}
                    </button>

                    <button type="button" className="auth-link" onClick={onForgotPassword}>
                        Forgot your password?
                    </button>
                </form>
            </div>
        </div>
    );
}
